import {
  Box,
  Button,
  Divider,
  PasswordInput,
  Stack,
  Text,
} from "@mantine/core";
import { useFormik } from "formik";
import * as Yup from "yup";
import { FC } from "react";

import { authApi } from "apis";
import PageContent from "components/layouts/PageContent";
import PageHeader from "components/layouts/PageHeader";
import { useNotification } from "utils/hooks";

const CHANGE_PASSWORD_TITLE = "\u0110\u1ed5i m\u1eadt kh\u1ea9u";
const OLD_PASSWORD_LABEL = "M\u1eadt kh\u1ea9u c\u0169";
const NEW_PASSWORD_LABEL = "M\u1eadt kh\u1ea9u m\u1edbi";
const CONFIRM_PASSWORD_LABEL = "X\u00e1c nh\u1eadn m\u1eadt kh\u1ea9u m\u1edbi";
const PASSWORD_REQUIRED_MESSAGE =
  "M\u1eadt kh\u1ea9u kh\u00f4ng \u0111\u01b0\u1ee3c \u0111\u1ec3 tr\u1ed1ng";
const PASSWORD_RULE_MESSAGE =
  "M\u1eadt kh\u1ea9u ph\u1ea3i c\u00f3 ch\u1eef hoa, ch\u1eef th\u01b0\u1eddng, s\u1ed1 v\u00e0 8 k\u00fd t\u1ef1.";
const CONFIRM_PASSWORD_REQUIRED_MESSAGE =
  "X\u00e1c nh\u1eadn m\u1eadt kh\u1ea9u kh\u00f4ng \u0111\u01b0\u1ee3c \u0111\u1ec3 tr\u1ed1ng.";
const CONFIRM_PASSWORD_INVALID_MESSAGE =
  "X\u00e1c nh\u1eadn m\u1eadt kh\u1ea9u kh\u00f4ng ch\u00ednh x\u00e1c.";
const CHANGE_PASSWORD_SUCCESS_TITLE =
  "\u0110\u1ed5i m\u1eadt kh\u1ea9u th\u00e0nh c\u00f4ng";
const CHANGE_PASSWORD_SUCCESS_MESSAGE =
  "Vui l\u00f2ng \u0111\u0103ng nh\u1eadp l\u1ea1i";
const CHANGE_PASSWORD_ERROR_TITLE =
  "\u0110\u1ed5i m\u1eadt kh\u1ea9u kh\u00f4ng th\u00e0nh c\u00f4ng";

const ChangePassword: FC = () => {
  const { notification } = useNotification();

  const form = useFormik({
    initialValues: {
      password: "",
      new_password: "",
      confirm_password: "",
    },
    validationSchema: Yup.object().shape({
      password: Yup.string()
        .required(PASSWORD_REQUIRED_MESSAGE)
        .matches(/^(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{8,}$/, PASSWORD_RULE_MESSAGE),
      new_password: Yup.string()
        .required(PASSWORD_REQUIRED_MESSAGE)
        .matches(/^(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{8,}$/, PASSWORD_RULE_MESSAGE),
      confirm_password: Yup.string()
        .required(CONFIRM_PASSWORD_REQUIRED_MESSAGE)
        .oneOf(
          [Yup.ref("new_password"), ""],
          CONFIRM_PASSWORD_INVALID_MESSAGE
        ),
    }),
    onSubmit: async (data) => {
      try {
        await authApi.changePassword(data);

        notification.success({
          title: CHANGE_PASSWORD_SUCCESS_TITLE,
          message: CHANGE_PASSWORD_SUCCESS_MESSAGE,
        });

        form.resetForm();
      } catch (error: any) {
        notification.error({
          title: CHANGE_PASSWORD_ERROR_TITLE,
          message: error?.data?.message,
        });
      }
    },
  });

  return (
    <Stack gap="md">
      <PageHeader
        title={CHANGE_PASSWORD_TITLE}
        breadcrumbs={[{ title: CHANGE_PASSWORD_TITLE }]}
        showDateFilter={false}
      />
      <PageContent p="lg">
        <Box maw={350} mx="auto">
          <Stack>
            <Text
              fz="xl"
              fw={700}
              variant="gradient"
              gradient={{ from: "indigo", to: "cyan", deg: 45 }}
            >
              {CHANGE_PASSWORD_TITLE.toUpperCase()}
            </Text>
            <Divider />
            <PasswordInput
              label={OLD_PASSWORD_LABEL}
              placeholder={OLD_PASSWORD_LABEL}
              name="password"
              required
              value={form.values.password}
              onChange={form.handleChange}
              error={
                form.errors.password && form.touched.password
                  ? form.errors.password
                  : ""
              }
            />
            <PasswordInput
              label={NEW_PASSWORD_LABEL}
              placeholder={NEW_PASSWORD_LABEL}
              name="new_password"
              required
              value={form.values.new_password}
              onChange={form.handleChange}
              error={
                form.errors.new_password && form.touched.new_password
                  ? form.errors.new_password
                  : ""
              }
            />
            <PasswordInput
              label={CONFIRM_PASSWORD_LABEL}
              placeholder={CONFIRM_PASSWORD_LABEL}
              name="confirm_password"
              required
              value={form.values.confirm_password}
              onChange={form.handleChange}
              error={
                form.errors.confirm_password && form.touched.confirm_password
                  ? form.errors.confirm_password
                  : ""
              }
            />
            <Button onClick={form.submitForm}>{CHANGE_PASSWORD_TITLE}</Button>
          </Stack>
        </Box>
      </PageContent>
    </Stack>
  );
};

export default ChangePassword;
